import React, { useState, useMemo } from 'react'
import { Box, Typography, IconButton, InputAdornment } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CommonTextField,
  CommonButton,
  CommonDialog,
} from '@/components/common'
import { useDonorContext } from '@/context'
import { useUpdateDonor, useResetPassword } from '@/hooks'
import { VisibilityIcon, VisibilityOffIcon } from '@/icons'
import { IconSuccess } from '@/assets/icons'
import { routeToRedirectUrl } from '@/utils'
import { STORAGE_KEYS, ROUTES } from '@/constants'

export const ResetPasswordPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { donor, getDonor } = useDonorContext()
  const { loadingUpdateDonor, updateDonor } = useUpdateDonor()
  const { loadingResetPassword, resetPassword } = useResetPassword()

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const redirectUrl = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const hasRedirectUrl = searchParams.has(STORAGE_KEYS.REDIRECT_URL)
    if (hasRedirectUrl) {
      return searchParams.get(STORAGE_KEYS.REDIRECT_URL)
    }
    return null
  }, [location.search])

  const handleUpdateDonor = async () => {
    try {
      await updateDonor(donor?.dp_donor_auth?.id, {
        require_password_reset: false,
      })
      await getDonor()
      if (redirectUrl) {
        routeToRedirectUrl(redirectUrl)
      } else {
        navigate(ROUTES.HOME)
      }
    } catch (err) {
      setError(err.message || 'Password update failed. Please try again.')
    }
  }

  const handleResetPassword = async () => {
    setError('')

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }

    try {
      await resetPassword({ password: newPassword })
      handleSuccess()
    } catch (err) {
      setError(err.message || 'Password reset failed. Please try again.')
    }
  }

  const handleSuccess = () => {
    setSuccess(true)
    setError('')
    setNewPassword('')
    setConfirmPassword('')
  }

  const handleSuccessDialogClose = async () => {
    setSuccess(false)
    await getDonor()
    navigate(ROUTES.HOME)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        component='form'
        onSubmit={handleResetPassword}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: 700,
          gap: 3,
        }}
      >
        <Typography
          variant='h5'
          sx={{
            textAlign: 'center',
            fontWeight: 600,
            color: 'text.primary',
          }}
        >
          Welcome Back!{' '}
          <Box component='span' sx={{ color: 'primary.main' }}>
            {donor?.dp_donor_auth?.email}
          </Box>
        </Typography>
        <Typography
          variant='body1'
          sx={{
            textAlign: 'center',
            mb: 3,
            color: 'text.secondary',
          }}
        >
          {redirectUrl
            ? 'Would you like to set your password?'
            : 'Would you like to reset your password?'}
        </Typography>
        <CommonTextField
          id='new-password'
          label='New Password'
          value={newPassword}
          setValue={setNewPassword}
          type={showNewPassword ? 'text' : 'password'}
          error={error}
          placeholder='Enter new password'
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle new password visibility'
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    onMouseDown={e => e.preventDefault()}
                    edge='end'
                    size='small'
                  >
                    {showNewPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <CommonTextField
          id='confirm-password'
          label='Confirm Password'
          value={confirmPassword}
          setValue={setConfirmPassword}
          type={showConfirmPassword ? 'text' : 'password'}
          error={error}
          placeholder='Confirm new password'
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle confirm password visibility'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    onMouseDown={e => e.preventDefault()}
                    edge='end'
                    size='small'
                  >
                    {showConfirmPassword ? (
                      <VisibilityOffIcon />
                    ) : (
                      <VisibilityIcon />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <CommonButton
          id='reset-password-button'
          label='Continue'
          onClick={handleResetPassword}
          loading={loadingResetPassword}
          disabled={!newPassword || !confirmPassword}
          color='primary'
          variant='primary'
          size='large'
          style={{ width: '100%' }}
        />
        {redirectUrl && (
          <>
            <Typography
              variant='h6'
              fontWeight={500}
              sx={{
                textAlign: 'center',
                my: 2,
              }}
            >
              If you dont want a password, you can always log in without a
              password
            </Typography>
            <CommonButton
              id='continue-as-guest-button'
              label={'Skip this step'}
              size='large'
              loading={loadingUpdateDonor}
              onClick={handleUpdateDonor}
              style={{ width: '100%' }}
            />
          </>
        )}
      </Box>

      <CommonDialog
        open={success}
        onClose={handleSuccessDialogClose}
        maxWidth='sm'
        fullWidth
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <img src={IconSuccess} alt='success' />
          </Box>
          <Box textAlign={'center'} mt={2}>
            <Typography variant='h6' color='primary'>
              Success
            </Typography>
            <Typography variant='body1' sx={{ my: 3 }}>
              Your password has been reset successfully
            </Typography>
            <CommonButton
              variant='primary'
              onClick={handleSuccessDialogClose}
              sx={{ px: 4 }}
            >
              Ok
            </CommonButton>
          </Box>
        </Box>
      </CommonDialog>
    </Box>
  )
}
